import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@repo/ui/card'
import { useT } from '@repo/ui/hooks'
import { cn } from '@repo/ui/utils'
import { CircleCheckBigIcon } from 'lucide-react'
import { memo } from 'react'

import {
  CollapsibleCard,
  CollapsibleCardContent,
  CollapsibleCardFooter,
  CollapsibleCardHeader,
  CollapsibleCardLeftButtons,
  CollapsibleCardTitle,
  ItemCreatedDate,
  ItemUpdatedDate,
} from '@/components/CollapsibleCard'
import {
  TaskCategories,
  TaskDeletable,
  TaskDoable,
  TaskDueDate,
  TaskLink,
} from '@/components/tasks/Task'
import { TaskCreator } from '@/components/tasks/TaskCreator'
import { TaskEditor } from '@/components/tasks/TaskEditor'
import { TaskList } from '@/components/tasks/TaskList'
import { useTaskChangeHandler } from '@/hooks'
import { Task } from '@/store/task'

export function CurrentTasks({
  className,
  tasksComingSoon,
  tasksCount,
  tasksDone,
  tasksOverdue,
  tasksWithoutDueDate,
}: {
  className?: string
  tasksComingSoon: Task[]
  tasksCount: number
  tasksDone: Task[]
  tasksOverdue: Task[]
  tasksWithoutDueDate: Task[]
}) {
  const { t } = useT({ keyPrefix: 'task.current' })

  return (
    <Card className={cn('flex flex-col', tasksCount === 0 && 'gap-4', className)}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription>
          {t('description', {
            count: tasksCount,
          })}
        </CardDescription>

        <CardAction>
          <TaskCreator />
        </CardAction>
      </CardHeader>

      <CardContent className="space-y-2">
        {tasksCount === 0 && <p className="text-primary-400">{t('emptyDescription')}</p>}

        <TaskList
          tasks={tasksOverdue}
          transkey="overdue"
          variant="highlight"
          taskChildSlot={(task) => <InlineTask key={task.id} task={task} />}
        />

        <TaskList
          tasks={tasksComingSoon}
          transkey="comingSoon"
          taskChildSlot={(task) => <InlineTask key={task.id} task={task} />}
        />

        <TaskList
          tasks={tasksWithoutDueDate}
          transkey="noDueDate"
          taskChildSlot={(task) => <InlineTask key={task.id} task={task} />}
        />

        <div className="h-12" />
        <TaskList
          tasks={tasksDone}
          transkey="doneStatus"
          taskChildSlot={(task) => <InlineTask key={task.id} task={task} />}
        />
      </CardContent>
    </Card>
  )
}

const InlineTask = memo(function ({ className, task }: { className?: string; task: Task }) {
  const { tc } = useT()
  const onTaskChange = useTaskChangeHandler(task)
  const isDone = !!task.archivedAt
  return (
    <CollapsibleCard className={className}>
      <CollapsibleCardHeader>
        <CollapsibleCardLeftButtons>
          <TaskDoable
            task={task}
            isDone={isDone}
            onClick={isDone ? onTaskChange('unarchive') : onTaskChange('archive')}
          />
        </CollapsibleCardLeftButtons>
        <CollapsibleCardTitle>{task.title}</CollapsibleCardTitle>
      </CollapsibleCardHeader>

      <CollapsibleCardContent>
        <div className="flex items-center justify-between gap-2">
          {task.archivedAt && (
            <div className="text-primary-500 inline-flex items-center gap-2">
              <CircleCheckBigIcon className="size-4" />
              {tc('doneStatus', { cfl: true })}
            </div>
          )}
          <ItemCreatedDate createdAt={task.createdAt} />
          <ItemUpdatedDate updatedAt={task.updatedAt} />
          <TaskDueDate
            isEditable={!isDone}
            initialDueDate={task.dueDate}
            onDueDateChange={(dueDate) => {
              onTaskChange('updateDueDate')(task.id, dueDate?.getTime())
            }}
          />
        </div>

        <div className="whitespace-pre-wrap text-pretty">{task.content}</div>

        {<TaskLink task={task} />}

        {<TaskCategories task={task} />}

        <CollapsibleCardFooter className="flex-col-reverse sm:flex-row">
          <TaskDeletable task={task} onClick={onTaskChange('delete')} />

          <TaskEditor task={task} />

          <TaskDoable
            className="text-inherit hover:text-inherit"
            isDone={isDone}
            isLabelShown
            onClick={isDone ? onTaskChange('unarchive') : onTaskChange('archive')}
            size="sm"
            task={task}
            variant="outline"
            withTooltip={false}
          />
        </CollapsibleCardFooter>
      </CollapsibleCardContent>
    </CollapsibleCard>
  )
})
